/**
 * Phase 105-Agent-A — RejectReasonModal.
 *
 * Radio list of canonical reasons (mirrors `_REJECT_REASONS` on the
 * backend) + optional free-text note. Submitting POSTs to
 * `/api/orders/reject/{tracking_id}`. Toast on success / error.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

// Mirror of `_REJECT_REASONS` in backend/routes/orders.py
const REASONS = [
  { key: "unreachable",   label: "Customer unreachable" },
  { key: "too_far",       label: "Too far from my location" },
  { key: "wrong_address", label: "Wrong / undeliverable address" },
  { key: "vehicle_issue", label: "Vehicle issue" },
  { key: "other",         label: "Other (specify below)" },
];

export default function RejectReasonModal({ open, onOpenChange, trackingId, onRejected }) {
  const [reason, setReason] = useState("");
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setReason(""); setFreeText(""); setBusy(false); };

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason to continue."); return; }
    setBusy(true);
    try {
      await api.post(`/orders/reject/${trackingId}`, {
        reason,
        free_text: freeText?.trim() ? freeText.trim() : undefined,
      });
      toast.success(`Task ${trackingId} returned to the pool.`);
      if (onRejected) onRejected(trackingId);
      onOpenChange(false);
      reset();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Couldn't reject the task.";
      toast.error(typeof detail === "string" ? detail : "Couldn't reject the task.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="reject-reason-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Reject this task?
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            The task <span className="font-mono font-semibold">{trackingId}</span> will be returned to the dispatcher pool for re-assignment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2" data-testid="reject-reason-list">
          {REASONS.map((r) => (
            <label
              key={r.key}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                reason === r.key ? "border-[#FF6B00] bg-orange-50/60" : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`reject-reason-${r.key}`}
            >
              <input
                type="radio"
                name="reject-reason"
                value={r.key}
                checked={reason === r.key}
                onChange={() => setReason(r.key)}
                className="h-4 w-4 accent-[#FF6B00]"
              />
              <span className="text-sm font-medium text-slate-800">{r.label}</span>
            </label>
          ))}
        </div>

        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value.slice(0, 280))}
          placeholder="Anything else dispatch should know? (optional, 280 chars)"
          rows={3}
          maxLength={280}
          className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B00] focus:border-[#FF6B00]"
          data-testid="reject-free-text"
        />

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); reset(); }}
            disabled={busy}
            data-testid="reject-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!reason || busy}
            title={!reason ? "Pick a reason first" : busy ? "Submitting…" : undefined}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reject-submit-btn"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Rejecting…</> : "Reject task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
